import axios from "axios";

import { apiFetch } from "./apiFetch";

let cache = new Map();
let inFlight = new Map();
const CACHE_DURATION = 3000; // 3s

export const createIAConfig = async ({
  NOME,
  FUNCAO,
  FOTO,
  TAG,
  API_KEY,
  DESCRICAO
}) => {
  try {
    if (!NOME) throw new Error("NOME obrigatório");
    if (!FUNCAO) throw new Error("FUNCAO obrigatória");

    const formData = new FormData();

    formData.append("NOME", NOME);
    formData.append("FUNCAO", FUNCAO);

    if (TAG) formData.append("TAG", TAG);
    if (API_KEY) formData.append("API_KEY", API_KEY);
    if (DESCRICAO) formData.append("DESCRICAO", DESCRICAO);
    if (FOTO) formData.append("FOTO", FOTO);

    const response = await apiFetch("/ia", {
      method: "POST",
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status}`);
    }

    return await response.json();

  } catch (error) {
    console.error("Erro ao criar IA config:", error);
    return { type: "error", message: error.message || "Erro desconhecido" };
  }
};

export const updateIAConfig = async ({
  STAMP,
  NOME,
  FUNCAO,
  FOTO,
  TAG,
  API_KEY,
  DESCRICAO
}) => {
  try {
    if (!STAMP) throw new Error("STAMP obrigatório");

    const formData = new FormData();

    if (NOME) formData.append("NOME", NOME);
    if (FUNCAO) formData.append("FUNCAO", FUNCAO);
    if (TAG) formData.append("TAG", TAG);
    if (API_KEY) formData.append("API_KEY", API_KEY);
    if (DESCRICAO) formData.append("DESCRICAO", DESCRICAO);

    if (FOTO instanceof File) {
      formData.append("FOTO", FOTO);
    }

    const response = await apiFetch(`/ia/${STAMP}`, {
      method: "PUT",
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status}`);
    }

    return await response.json();

  } catch (error) {
    console.error("Erro ao atualizar IA config:", error);
    return null;
  }
};

export const getIAConfig = async (
  limit = 20,
  before = null,
  search = ""
) => {
  try {
    const key = `${limit}|${before || "null"}|${search || ""}`;
    const now = Date.now();

    // Retorna cache se ainda está válido
    if (cache.has(key)) {
      const cached = cache.get(key);
      if (now - cached.time < CACHE_DURATION) {
        return cached.data;
      }
    }

    // Se já há requisição em voo, reutiliza
    if (inFlight.has(key)) {
      return inFlight.get(key);
    }

    let url = `/ia?limit=${limit}`;

    if (before?.trim?.()) {
      url += `&before=${encodeURIComponent(before)}`;
    }

    if (search?.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    const promise = (async () => {
      try {
        const response = await apiFetch(url);

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`);
        }

        const data = await response.json();

        const ia = (data.ia || []).map((item) => ({
          ...item,
          FOTO: item.FOTO || null,
        }));

        const result = {
          ia,
          hasMore: data.hasMore,
        };

        cache.set(key, {
          data: result,
          time: Date.now(),
        });

        return result;
      } finally {
        inFlight.delete(key);
      }
    })();

    inFlight.set(key, promise);
    return promise;

  } catch (err) {
    console.error("Erro ao buscar IA configs:", err);
    return {
      ia: [],
      hasMore: false,
    };
  }
};

export const getIAConfigs = async () => {
  try {
    const now = Date.now();
    const cacheKey = "getIAConfigs";

    // Retorna cache se ainda está válido
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (now - cached.time < CACHE_DURATION) {
        return cached.data;
      }
    }

    // Se já há requisição em voo, reutiliza
    if (inFlight.has(cacheKey)) {
      return inFlight.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const response = await apiFetch("/ia");

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`);
        }

        const data = await response.json();

        const ias = (data.ia || []).map((item) => ({
          ...item,
          FOTO: item.FOTO || null,
        }));

        const result = {
          ias,
        };

        cache.set(cacheKey, {
          data: result,
          time: Date.now(),
        });

        return result;
      } finally {
        inFlight.delete(cacheKey);
      }
    })();

    inFlight.set(cacheKey, promise);
    return promise;

  } catch (err) {
    console.error("Erro ao buscar todas as IAs:", err);
    return {
      ias: [],
    };
  }
};


export const deleteIAConfig = async (STAMP) => {
  try {
    if (!STAMP) {
      throw new Error("STAMP obrigatório");
    }

    const response = await apiFetch(`/ia/${STAMP}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Erro na API: ${response.status}`);
    }

    return await response.json();

  } catch (error) {
    console.error("Erro ao eliminar IA config:", error);
    return null;
  }
};


export const getIALogs = async (STAMP, limit = 10) => {
  try {
    if (!STAMP) throw new Error("STAMP obrigatório");

    const cacheKey = `logs|${STAMP}|${limit}`;
    const now = Date.now();

  
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      if (now - cached.time < 2000) {
        return cached.data;
      }
    }

  
    if (inFlight.has(cacheKey)) {
      return inFlight.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const response = await apiFetch(
          `/ia/${STAMP}/logs?limit=${limit}`
        );

        if (!response.ok) {
          throw new Error(`Erro na API: ${response.status}`);
        }

        const data = await response.json();

        const result = {
          logs: data.logs || [],
        };

        cache.set(cacheKey, {
          data: result,
          time: Date.now(),
        });

        return result;

      } finally {
        inFlight.delete(cacheKey);
      }
    })();

    inFlight.set(cacheKey, promise);
    return promise;

  } catch (error) {
    console.error("Erro ao buscar logs IA:", error);
    return {
      logs: [],
    };
  }
};